import { useCustom, useCustomMutation, useGo, useApiUrl } from '@refinedev/core';
import { useParams } from 'react-router-dom';
import { Loader2, Webhook, Copy, Check, Settings } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { FormHeader } from '@/components/forms';
import { CustomFieldsEditor } from './components/CustomFieldsEditor';
import type { LeadWebhookWithFields } from '../types';

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function LeadWebhooksShow() {
  const go = useGo();
  const apiUrl = useApiUrl();
  const { id } = useParams<{ id: string }>();
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState('');

  const webhookQuery = useCustom<LeadWebhookWithFields>({
    url: `/leads/webhooks/${id ?? ''}`,
    method: 'get',
  });

  const isLoading = webhookQuery.query?.isLoading ?? false;
  const refetch = webhookQuery.query?.refetch;
  const webhook = webhookQuery.query?.data?.data;

  const updateMutation = useCustomMutation();

  const handleToggleActive = (value: string) => {
    if (!webhook) { return; }
    updateMutation.mutate(
      {
        url: `/leads/webhooks/${webhook.id}`,
        method: 'patch',
        values: { isActive: value === 'true' },
      },
      { onSuccess: () => refetch?.() },
    );
  };

  const handleSaveName = () => {
    if (!webhook || !editName.trim()) { return; }
    updateMutation.mutate(
      {
        url: `/leads/webhooks/${webhook.id}`,
        method: 'patch',
        values: { name: editName.trim() },
      },
      {
        onSuccess: () => {
          setIsEditingName(false);
          refetch?.();
        },
      },
    );
  };

  const handleCopyUrl = () => {
    const baseUrl = apiUrl.replace('/api', '') || window.location.origin;
    const webhookUrl = `${baseUrl}/webhook/leads/<TOKEN>`;
    void navigator.clipboard.writeText(webhookUrl).then(() => {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!webhook) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Webhook не найден</p>
        <Button variant="link" onClick={() => go({ to: '/leads/webhooks' })}>Вернуться к списку</Button>
      </div>
    );
  }

  const fieldMapping = webhook.fieldMapping ?? {};

  return (
    <div className="space-y-4">
      <FormHeader
        title={webhook.name}
        description={`Webhook #${webhook.id}`}
        onBack={() => go({ to: '/leads/webhooks' })}
        icon={<Webhook className="h-5 w-5" />}
        titleAddon={
          <Badge variant={webhook.isActive ? 'default' : 'secondary'}>
            {webhook.isActive ? 'Активен' : 'Отключён'}
          </Badge>
        }
        sticky
        actions={
          <Select
            value={String(webhook.isActive)}
            onValueChange={handleToggleActive}
          >
            <SelectTrigger className="h-9 w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">Активен</SelectItem>
              <SelectItem value="false">Отключён</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Left column: settings */}
        <div className="md:col-span-2 space-y-4">
          {/* Webhook URL instructions */}
          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Settings className="h-3.5 w-3.5" />
                URL для отправки данных
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-2 space-y-3">
              <p className="text-xs text-muted-foreground">
                При создании webhook токен был показан единожды. Используйте следующий формат URL:
              </p>
              <div className="flex items-center gap-2 bg-muted rounded-md p-3">
                <code className="flex-1 text-xs font-mono break-all">
                  {apiUrl.replace('/api', '') || window.location.origin}/webhook/leads/{'<TOKEN>'}
                </code>
                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={handleCopyUrl}>
                  {copiedUrl ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Метод: <code className="bg-muted px-1 rounded font-mono">POST</code> •
                Тип: <code className="bg-muted px-1 rounded font-mono">application/json</code> •
                Опциональный параметр: <code className="bg-muted px-1 rounded font-mono">?utm_source=название</code>
              </p>
            </CardContent>
          </Card>

          {/* Field mapping */}
          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-sm">Маппинг полей</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-2">
              {Object.keys(fieldMapping).length === 0 ? (
                <p className="text-sm text-muted-foreground">Маппинг не настроен — все данные попадут в customData</p>
              ) : (
                <div className="space-y-1">
                  {Object.entries(fieldMapping).map(([leadField, sourceField]) => (
                    <div key={leadField} className="flex items-center gap-2 text-xs">
                      <code className="font-mono bg-muted px-1.5 py-0.5 rounded">{sourceField}</code>
                      <span className="text-muted-foreground">→</span>
                      <code className="font-mono bg-primary/10 text-primary px-1.5 py-0.5 rounded">{leadField}</code>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Custom Fields */}
          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-sm">Кастомные поля</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-2">
              <CustomFieldsEditor
                webhookId={webhook.id}
                fields={webhook.customFields ?? []}
                onRefetch={() => refetch?.()}
              />
            </CardContent>
          </Card>
        </div>

        {/* Right column: meta */}
        <div className="space-y-4">
          {/* Basic info */}
          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-sm">Информация</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-2 space-y-3">
              {/* Name edit */}
              <div>
                <p className="text-xs text-muted-foreground mb-1">Название</p>
                {isEditingName ? (
                  <div className="flex gap-1">
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="h-7 text-xs flex-1"
                    />
                    <Button size="sm" className="h-7 text-xs" onClick={handleSaveName} disabled={updateMutation.mutation.isPending}>
                      OK
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setIsEditingName(false)}>
                      Нет
                    </Button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="text-sm font-medium hover:underline text-left"
                    onClick={() => { setEditName(webhook.name); setIsEditingName(true); }}
                  >
                    {webhook.name}
                  </button>
                )}
              </div>

              <Separator />

              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Создан</span>
                <span>{formatDate(webhook.createdAt)}</span>
              </div>

              <Separator />

              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Обновлён</span>
                <span>{formatDate(webhook.updatedAt)}</span>
              </div>

              <Separator />

              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Custom fields</span>
                <span>{webhook.customFields?.length ?? 0}</span>
              </div>
            </CardContent>
          </Card>

          {/* Default tags */}
          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-sm">Теги по умолчанию</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-2">
              {webhook.defaultTagIds && webhook.defaultTagIds.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {webhook.defaultTagIds.map((tagId) => (
                    <Badge key={tagId} variant="secondary" className="text-xs">
                      Tag #{tagId}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Нет тегов по умолчанию</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
